import * as d3 from 'd3';
import React, { useEffect, useState } from 'react';
import { Checkbox } from 'react-bootstrap';
import { decamelizeKeys } from 'humps';
import SubHeader from '../SubHeader.jsx';
import Chart from './Chart.js';
import UnitsChart from './UnitsChart.js';
import MileMarkersChart from './MileMarkersChart.js';
import TimelineTypeSelect from './TimelineTypeSelect.jsx';
import TimelineScaleTypeSelect from './TimelineScaleTypeSelect.jsx';
import { getOrgTermsContent } from '../../content/utils.js';
import styles from './index.module.css';
import withFullscreen from '../common/withFullscreen.jsx';

const GanttChartV4 = ({
  results,
  onShowMilestoneModal,
  reportOptions,
  onUpdateReportOptions,
  ganttRef,
  hasParentPortfolios,
  onToggleFullscreen,
  isFullscreen,
  query,
  chartContainerId = 'ganttExportContainer',
}) => {
  const { parentPortfolios, portfolios, projects } = results;

  const { scaleType, timelineType, chartStartDate, showMilestones } =
    reportOptions;

  const [charts, setCharts] = useState(null);
  const [exportCharts, setExportCharts] = useState(null);

  useEffect(() => {
    if (charts) {
      updateChartsOptions(
        'minHeight',
        isFullscreen ? window.innerHeight - 120 : 300
      );
      updateChartsOptions('width', window.innerWidth - 1);
      callChartsActions('rebuild');
    }
  }, [isFullscreen]);

  const filteredByDraftStatus = (arr) =>
    arr.filter((initiative) => !initiative.draft);

  const getAllPortfolioAndProjectMilestones = () => {
    const allMilestones = [
      ...projects.flatMap((p) => p.milestones || []),
      ...portfolios.flatMap((p) => p.milestones || []),
    ];

    if (parentPortfolios) {
      allMilestones.push(
        ...parentPortfolios.flatMap((pp) => pp.milestones || [])
      );
    }

    return decamelizeKeys(allMilestones);
  };

  const groupMilestonesByDate = (arr) => {
    const groupedMilestones = {};
    arr.forEach((milestone) => {
      const date_at = new Date(milestone.date_at);
      const dateKey = date_at.toISOString().split('T')[0];

      if (!groupedMilestones[dateKey]) {
        groupedMilestones[dateKey] = [];
      }

      groupedMilestones[dateKey].push(milestone);
    });

    return Object.entries(groupedMilestones).map(
      ([date_at, childMilestone]) => ({
        date_at,
        childMilestone,
      })
    );
  };

  const sortByStartDate = (arr) =>
    arr.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

  const getData = () => {
    // Use hierarchical parent portfolios if available, otherwise fall back to flat portfolios
    const dataSource = parentPortfolios?.length > 0 ? parentPortfolios : null;

    if (dataSource) {
      // Hierarchical parent portfolio structure
      return dataSource.flatMap((parentPortfolio) => {
        const result = [];

        // Add parent portfolio row
        if (timelineType === 'all' || timelineType === 'parentPortfolios') {
          result.push({
            parentPortfolio: { id: parentPortfolio.id },
            title:
              parentPortfolio.name ||
              `No ${getOrgTermsContent('parentPortfolio')}`,
            startDate:
              parentPortfolio.startDate || new Date('1970-01-01T00:00:00.000Z'),
            endDate:
              parentPortfolio.endDate || new Date('2077-01-01T00:00:00.000Z'),
            type: 'parentPortfolio',
          });
        }

        // Process portfolios under parent portfolio
        (parentPortfolio?.portfolios || []).forEach((portfolio) => {
          if (timelineType === 'all' || timelineType === 'portfolios') {
            result.push({
              portfolio: { id: portfolio.id },
              title: portfolio.name || `No ${getOrgTermsContent('portfolio')}`,
              startDate:
                portfolio.startDate || new Date('1970-01-01T00:00:00.000Z'),
              endDate:
                portfolio.endDate || new Date('2077-01-01T00:00:00.000Z'),
              type: 'portfolio',
            });
          }

          // Process projects under portfolio
          (portfolio.projects || []).forEach((project) => {
            if (timelineType === 'all' || timelineType === 'projects') {
              result.push({
                project: { id: project.id },
                title: project.name,
                startDate: project.startDate,
                endDate: project.endDate,
                type: 'project',
                impact: project.impact,
              });
            }

            if (timelineType === 'all' || timelineType === 'initiatives') {
              const initiatives = sortByStartDate([
                ...filteredByDraftStatus(
                  project.communicationInitiatives || []
                ),
                ...filteredByDraftStatus(project.readinessInitiatives || []),
                ...filteredByDraftStatus(project.trainingInitiatives || []),
                ...filteredByDraftStatus(project.sustainmentInitiatives || []),
              ]).map((init) => ({
                ...init,
                type: 'initiative',
              }));

              result.push(...initiatives);
            }
          });
        });

        return result;
      });
    } else {
      // Flat portfolio structure (backward compatibility)
      return portfolios.flatMap((portfolio) => {
        const result = [];

        if (timelineType === 'all' || timelineType === 'portfolios') {
          result.push({
            portfolio: { id: portfolio.id },
            title: portfolio.name || `No ${getOrgTermsContent('portfolio')}`,
            startDate:
              portfolio.startDate || new Date('1970-01-01T00:00:00.000Z'),
            endDate: portfolio.endDate || new Date('2077-01-01T00:00:00.000Z'),
            type: 'portfolio',
          });
        }

        (portfolio.projects || []).forEach((project) => {
          if (timelineType === 'all' || timelineType === 'projects') {
            result.push({
              project: { id: project.id },
              title: project.name,
              startDate: project.startDate,
              endDate: project.endDate,
              type: 'project',
              impact: project.impact,
            });
          }

          if (timelineType === 'all' || timelineType === 'initiatives') {
            const initiatives = sortByStartDate([
              ...filteredByDraftStatus(project.communicationInitiatives || []),
              ...filteredByDraftStatus(project.readinessInitiatives || []),
              ...filteredByDraftStatus(project.trainingInitiatives || []),
              ...filteredByDraftStatus(project.sustainmentInitiatives || []),
            ]).map((init) => ({
              ...init,
              type: 'initiative',
            }));

            result.push(...initiatives);
          }
        });

        return result;
      });
    }
  };

  const updateChartsOptions = (key, val) => {
    charts &&
      charts.forEach((chart) => {
        chart.options[key] = val;
      });
  };

  const callChartsActions = (key) => {
    charts && charts.forEach((chart) => chart[key]());
  };

  const getOffsetData = () => {
    switch (scaleType) {
      case 'week':
        return { key: 'timeWeek', steps: 1 };
      case 'month':
        return { key: 'timeMonth', steps: 1 };
      case 'quarter':
        return { key: 'timeMonth', steps: 3 };
      case 'year':
        return { key: 'timeMonth', steps: 12 };
      default:
        return { key: 'timeWeek', steps: 2 };
    }
  };

  const current = () => {
    const newDate = Date.now();
    onUpdateReportOptions({ chartStartDate: newDate });
    updateChartsOptions('startDate', newDate);
    callChartsActions('update');
  };

  const prev = () => {
    const offsetData = getOffsetData();
    const newDate = Date.parse(
      d3[offsetData.key].offset(chartStartDate, -offsetData.steps)
    );
    onUpdateReportOptions({ chartStartDate: newDate });
    updateChartsOptions('startDate', newDate);
    callChartsActions('update');
  };

  const next = () => {
    const offsetData = getOffsetData();
    const newDate = Date.parse(
      d3[offsetData.key].offset(chartStartDate, offsetData.steps)
    );
    onUpdateReportOptions({ chartStartDate: newDate });
    updateChartsOptions('startDate', newDate);
    callChartsActions('update');
  };

  const year = () => {
    onUpdateReportOptions({ scaleType: 'year' });
    updateChartsOptions('scaleType', 'year');
    updateChartsOptions('monthCount', 48);
    callChartsActions('rebuild');
  };

  const quarter = () => {
    onUpdateReportOptions({ scaleType: 'quarter' });
    updateChartsOptions('scaleType', 'quarter');
    updateChartsOptions('monthCount', 12);
    callChartsActions('rebuild');
  };

  const month = () => {
    onUpdateReportOptions({ scaleType: 'month' });
    updateChartsOptions('scaleType', 'month');
    updateChartsOptions('monthCount', 4);
    callChartsActions('rebuild');
  };

  const week = () => {
    onUpdateReportOptions({ scaleType: 'week' });
    updateChartsOptions('scaleType', 'week');
    callChartsActions('rebuild');
  };

  const toggleShowMilestones = () => {
    onUpdateReportOptions({ showMilestones: !showMilestones });
    updateChartsOptions('showMilestones', !showMilestones);
    callChartsActions('rebuild');
  };

  const resizeChart = () => {
    updateChartsOptions('width', document.body.clientWidth - 1);
    callChartsActions('rebuild');
  };

  useEffect(() => {
    const monthCount = {
      week: 4,
      month: 4,
      quarter: 12,
      year: 48,
    };

    const options = {
      width: document.body.clientWidth - 1,
      padding: 10,
      parentPortfolioBarHeight: 28,
      portfolioBarHeight: 28,
      projectBarHeight: 24,
      initiativeBarHeight: 16,
      weekCount: 4,
      monthCount: monthCount[scaleType],
      startDate: chartStartDate,
      scaleType,
      showMilestones,
      minHeight: 300,
    };

    const uniqueProjectIds = projects.map((p) => p.id);
    const data = getData();
    const milestonesFromAllCharts = groupMilestonesByDate(
      getAllPortfolioAndProjectMilestones()
    );

    const unitsChart = new UnitsChart('#ganttChart-units', [], options);
    const unitsChartPdf = new UnitsChart('#ganttChart-units-pdf', [], {
      ...options,
      width: 804,
    });

    const mileMarkersChart = new MileMarkersChart(
      '#ganttChart-milestones',
      { milestonesFromAllCharts },
      { ...options, onShowMilestoneModal }
    );

    const mileMarkersChartPdf = new MileMarkersChart(
      '#ganttChart-milestones-pdf',
      { milestonesFromAllCharts },
      { ...options, width: 804, onShowMilestoneModal }
    );

    const chart = new Chart(
      '#ganttChart',
      { rows: data, lines: milestonesFromAllCharts },
      { ...options, uniqueProjectIds }
    );

    const chartPdf = new Chart(
      '#ganttChartPdf',
      { rows: data, lines: milestonesFromAllCharts },
      { ...options, width: 804, uniqueProjectIds }
    );

    setCharts([
      unitsChart,
      mileMarkersChart,
      chart,
      unitsChartPdf,
      mileMarkersChartPdf,
      chartPdf,
    ]);
  }, [projects, timelineType]);

  useEffect(() => {
    if (charts) {
      callChartsActions('build');
    }
  }, [charts]);

  useEffect(() => {
    if (charts) {
      current();
    }
  }, [scaleType]);

  useEffect(() => {
    const PIXELS_PER_MONTH = { week: 700, month: 200, quarter: 100, year: 50 };
    const PADDING_MONTHS = { week: 0, month: 1, quarter: 2, year: 2 };
    const MIN_EXPORT_WIDTH = 800;
    const MAX_EXPORT_WIDTH = 8000;
    const SENTINEL_MIN = new Date('1990-01-01').getTime();
    const SENTINEL_MAX = new Date('2070-01-01').getTime();
    const MAX_MONTHS_BY_SCALE = {
      week: 6,
      month: 36,
      quarter: 120,
      year: Infinity,
    };
    const SCALE_PROMOTION = {
      week: 'month',
      month: 'quarter',
      quarter: 'year',
      year: 'year',
    };

    const baseOptions = {
      padding: 10,
      parentPortfolioBarHeight: 28,
      portfolioBarHeight: 28,
      projectBarHeight: 24,
      initiativeBarHeight: 16,
      showMilestones,
      minHeight: 300,
    };

    const uniqueProjectIds = projects.map((p) => p.id);
    const data = getData();
    const milestonesFromAllCharts = groupMilestonesByDate(
      getAllPortfolioAndProjectMilestones()
    );
    const projectsWithDates = projects.filter((p) => {
      if (!p.startDate || !p.endDate) return false;
      const s = new Date(p.startDate).getTime();
      const e = new Date(p.endDate).getTime();
      if (Number.isNaN(s) || Number.isNaN(e)) return false;
      return s > SENTINEL_MIN && e < SENTINEL_MAX;
    });
    const now = Date.now();

    const dateFieldsByTimeline = {
      initiatives: ['initiativeStartDate', 'initiativeEndDate'],
      projects: ['projectStartDate', 'projectEndDate'],
      portfolios: ['projectStartDate', 'projectEndDate'],
      parentPortfolios: ['projectStartDate', 'projectEndDate'],
      all: ['projectStartDate', 'projectEndDate'],
    };
    const [startField, endField] =
      dateFieldsByTimeline[timelineType] || dateFieldsByTimeline.all;

    const queryStart = query?.[startField] ? new Date(query[startField]) : null;
    const queryEnd = query?.[endField] ? new Date(query[endField]) : null;

    const milestoneDates = milestonesFromAllCharts
      .map((m) => new Date(m.date_at).getTime())
      .filter((t) => !Number.isNaN(t) && t > SENTINEL_MIN && t < SENTINEL_MAX);

    const rawMin = queryStart
      ? queryStart
      : new Date(
          Math.min(
            ...(projectsWithDates.length
              ? projectsWithDates.map((p) => new Date(p.startDate).getTime())
              : [now]),
            ...milestoneDates
          )
        );

    const rawMax = queryEnd
      ? queryEnd
      : new Date(
          Math.max(
            ...(projectsWithDates.length
              ? projectsWithDates.map((p) => new Date(p.endDate).getTime())
              : [now]),
            ...milestoneDates
          )
        );

    const rawMonthsSpan = Math.ceil(
      (rawMax - rawMin) / (1000 * 60 * 60 * 24 * 30)
    );

    let effectiveScale = scaleType;
    while (rawMonthsSpan > MAX_MONTHS_BY_SCALE[effectiveScale]) {
      const next = SCALE_PROMOTION[effectiveScale];
      if (next === effectiveScale) break;
      effectiveScale = next;
    }

    const padMonths = PADDING_MONTHS[effectiveScale] ?? 1;
    const minDate = d3.timeMonth.offset(rawMin, -padMonths);
    const maxDate = d3.timeMonth.offset(rawMax, padMonths);

    const totalMonths =
      Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24 * 30)) + 2;
    const totalWeeks =
      Math.ceil((maxDate - minDate) / (1000 * 60 * 60 * 24 * 7)) + 2;

    const computedWidth =
      totalMonths * (PIXELS_PER_MONTH[effectiveScale] || 100);
    const exportWidth = Math.min(
      Math.max(computedWidth, MIN_EXPORT_WIDTH),
      MAX_EXPORT_WIDTH
    );

    const exportOptions = {
      ...baseOptions,
      scaleType: effectiveScale,
      width: exportWidth,
      monthCount: totalMonths,
      weekCount: totalWeeks,
      startDate: minDate.getTime(),
    };

    const unitsExport = new UnitsChart(
      '#ganttChart-units-export',
      [],
      exportOptions
    );
    const mileMarkersExport = new MileMarkersChart(
      '#ganttChart-milestones-export',
      { milestonesFromAllCharts },
      { ...exportOptions, onShowMilestoneModal }
    );
    const chartExport = new Chart(
      '#ganttChartExport',
      { rows: data, lines: milestonesFromAllCharts },
      { ...exportOptions, uniqueProjectIds }
    );

    setExportCharts([unitsExport, mileMarkersExport, chartExport]);
  }, [
    projects,
    timelineType,
    scaleType,
    query?.projectStartDate,
    query?.projectEndDate,
    query?.initiativeStartDate,
    query?.initiativeEndDate,
    showMilestones,
  ]);

  useEffect(() => {
    if (exportCharts) exportCharts.forEach((c) => c.build());
  }, [exportCharts]);

  window.addEventListener('resize', resizeChart);

  return (
    <section className={isFullscreen ? 'fullscreen' : ''}>
      <SubHeader flexJustify small>
        <div className={`flex ${styles.flex}`}>
          <h2>Gantt Chart</h2>
          <div className="gantt-pager header-item">
            <button className="page prev" onClick={prev}>
              Previous
            </button>
            <button className="page today" onClick={current}>
              Today
            </button>
            <button className="page next" onClick={next}>
              Next
            </button>
          </div>
        </div>
        <div className={`flex flex-align-center ${styles.flex}`}>
          <div className="m-r-18">
            <Checkbox
              className="label-left"
              checked={isFullscreen}
              onChange={onToggleFullscreen}
            >
              <span className="text-muted font-size-14">Fullscreen</span>
              <span className="checkmark"></span>
            </Checkbox>
          </div>
          <div className="m-r-18">
            <Checkbox
              className="label-left"
              checked={showMilestones}
              onChange={toggleShowMilestones}
            >
              <span className="text-muted font-size-14">Show Milestones</span>
              <span className="checkmark"></span>
            </Checkbox>
          </div>
          <div className="m-r-18">
            <TimelineScaleTypeSelect
              scaleType={scaleType}
              onSelect={{ week, month, quarter, year }}
            />
          </div>
          <TimelineTypeSelect
            timelineType={timelineType}
            onSelect={onUpdateReportOptions}
            hasParentPortfolios={hasParentPortfolios}
          />
        </div>
      </SubHeader>

      <div>
        <div className="ganttWrapper">
          <div id="ganttChart-units" style={{ display: 'inline-flex' }} />
        </div>
        <div className="ganttWrapper">
          <div id="ganttChart-milestones" style={{ display: 'inline-flex' }} />
        </div>

        <div
          className="component component-bg-white"
          style={{
            height: isFullscreen ? 'calc(100vh - 60px)' : '350px',
            overflowY: 'scroll',
            overflowX: 'hidden',
            padding: 0,
            paddingBottom: '150px',
            background: '#fff',
          }}
        >
          <div className="ganttWrapper">
            <div id="tooltip" className="tooltip">
              <div id="type" />
              <h3 id="title" />
              <strong id="category" />
            </div>
            <div id="ganttChart" style={{ display: 'inline-flex' }} />
          </div>
        </div>
      </div>

      <div style={{ height: 0, overflowY: 'hidden' }}>
        <div ref={ganttRef}>
          <div className="ganttWrapper">
            <div id="ganttChart-units-pdf" style={{ display: 'inline-flex' }} />
          </div>
          <div className="ganttWrapper">
            <div
              id="ganttChart-milestones-pdf"
              style={{ display: 'inline-flex' }}
            />
          </div>

          <div
            className="component component-bg-white"
            style={{
              height: 'auto',
              overflowY: 'auto',
              overflowX: 'auto',
              padding: 0,
              paddingBottom: '0',
              background: '#fff',
            }}
          >
            <div className="ganttWrapper">
              <div id="ganttChartPdf" style={{ display: 'inline-flex' }} />
            </div>
          </div>
        </div>
      </div>

      <div
        style={{
          position: 'fixed',
          top: '-9999px',
          left: '-9999px',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <div id={chartContainerId}>
          <div className="ganttWrapper">
            <div
              id="ganttChart-units-export"
              style={{ display: 'inline-flex' }}
            />
          </div>
          <div className="ganttWrapper">
            <div
              id="ganttChart-milestones-export"
              style={{ display: 'inline-flex' }}
            />
          </div>
          <div className="ganttWrapper">
            <div id="ganttChartExport" style={{ display: 'inline-flex' }} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default withFullscreen(GanttChartV4, { defaultHeight: 350 });
